import React, { useMemo, useState } from "react";
import { Checkbox, FormControlLabel } from "@material-ui/core";
import { bool, func } from "prop-types";
import cogoToast from "cogo-toast";
import CustomButton from "../components/CustomButton";
import CustomInput from "../components/CustomInput";
import CustomSelect from "../components/CustomSelect";
import SlidingPane from "react-sliding-pane";
import "react-sliding-pane/dist/react-sliding-pane.css";

const RequestHiringSupport = ({ open, onClose }) => {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    companyName: "",
    roleTitle: "",
    seniority: "",
    location: "",
    confidentiality: "",
    timeline: "",
    otherReason: "",
  });

  const [reasons, setReasons] = useState({
    noTime: false,
    needExpertise: false,
    confidential: false,
    fullProcess: false,
    other: false,
  });

  const reasonOptions = useMemo(
    () => [
      { key: "noTime", label: "No time to run the search" },
      { key: "needExpertise", label: "Need expertise identifying the right talent" },
      { key: "confidential", label: "Role is confidential" },
      { key: "fullProcess", label: "Prefer workbrook to manage the full process" },
      { key: "other", label: "Other" },
    ],
    []
  );

  const handleFormChange = ({ target: { name, value } }) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleReasonToggle = (key) => {
    setReasons((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.fullName || !form.email || !form.roleTitle) {
      cogoToast.error("Please fill Full Name, Email Address, and Role Title.");
      return;
    }
    cogoToast.success("Request submitted. A Workbrook advisor will reach out within 24 hours.");
    if (typeof onClose === "function") onClose();
  };

  return (
    <SlidingPane
      className=""
      overlayClassName="some-custom-overlay-class"
      isOpen={open}
      title="Request Hiring Support"
      subtitle="Confidential. Fast. Tailored to your needs."
      width={window.innerWidth <= 1024 ? "100%" : "90%"}
      from="right"
      onRequestClose={onClose}
    >
      <div style={{ padding: "24px 32px", maxWidth: "960px", margin: "0 auto" }}>
        <p style={{ fontSize: "13px", color: "#666", marginBottom: "20px", marginTop: 0 }}>
          Share a few details and a workbrook advisor will reach out within 24 hours.
        </p>

        <form onSubmit={handleSubmit}>

          {/* ── Row 1: Contact + Role Overview ── */}
          <div className="row" style={{ marginBottom: 0 }}>

            {/* Contact */}
            <div className="col-12 col-md-6">
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Contact</div>
              <CustomInput
                id="rhs_fullName"
                name="fullName"
                label="Full Name"
                type="text"
                value={form.fullName}
                onChange={handleFormChange}
                isRequired
              />
              <CustomInput
                id="rhs_email"
                name="email"
                label="Email Address"
                type="email"
                value={form.email}
                onChange={handleFormChange}
                isRequired
              />
              <CustomInput
                id="rhs_companyName"
                name="companyName"
                label="Company Name (optional)"
                type="text"
                value={form.companyName}
                onChange={handleFormChange}
              />
            </div>

            {/* Role Overview */}
            <div className="col-12 col-md-6">
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Role Overview</div>
              <CustomInput
                id="rhs_roleTitle"
                name="roleTitle"
                label="Role Title"
                type="text"
                value={form.roleTitle}
                onChange={handleFormChange}
                isRequired
              />
              {/* Seniority — pull up to remove gap */}
              <div style={{ marginTop: "-10px" }}>
                <CustomSelect
                  handleChange={handleFormChange}
                  options={{
                    "": "Select Seniority Level",
                    Entry: "Entry",
                    Mid: "Mid",
                    Senior: "Senior",
                    Executive: "Executive",
                  }}
                  id="rhs_seniority"
                  name="seniority"
                  label="Seniority Level"
                  currentValue={form.seniority}
                />
              </div>
              <div style={{ marginTop: "-10px" }}>
                <CustomInput
                  id="rhs_location"
                  name="location"
                  label="Location"
                  type="text"
                  value={form.location}
                  onChange={handleFormChange}
                  placeholder="City, country, or remote"
                />
              </div>
            </div>
          </div>

          {/* ── Row 2: Why You Need Support + Confidentiality & Timeline ── */}
          <div className="row" style={{ marginTop: "8px" }}>

            {/* Why You Need Support */}
            <div className="col-12 col-md-6">
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Why You Need Support</div>
              {reasonOptions.map((opt) => (
                <div key={opt.key} style={{ marginBottom: "-6px" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={reasons[opt.key]}
                        onChange={() => handleReasonToggle(opt.key)}
                        color="primary"
                        style={{ padding: "6px 9px" }}
                      />
                    }
                    label={<span style={{ fontSize: "14px" }}>{opt.label}</span>}
                  />
                </div>
              ))}
              {reasons.other && (
                <div style={{ marginTop: "8px" }}>
                  <CustomInput
                    id="rhs_otherReason"
                    name="otherReason"
                    label="Please specify"
                    type="text"
                    value={form.otherReason}
                    onChange={handleFormChange}
                  />
                </div>
              )}
            </div>

            {/* Confidentiality & Timeline */}
            <div className="col-12 col-md-6">
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Confidentiality</div>
              <div style={{ marginTop: "-10px" }}>
                <CustomSelect
                  handleChange={handleFormChange}
                  options={{
                    "": "How sensitive is this search?",
                    Low: "Low",
                    Medium: "Medium",
                    High: "High (fully confidential)",
                  }}
                  id="rhs_confidentiality"
                  name="confidentiality"
                  label="Confidentiality Level"
                  currentValue={form.confidentiality}
                />
              </div>

              <div style={{ fontWeight: 700, marginTop: "8px", marginBottom: 4 }}>Timeline</div>
              <div style={{ marginTop: "-10px" }}>
                <CustomSelect
                  handleChange={handleFormChange}
                  options={{
                    "": "When do you need this hire?",
                    Immediately: "Immediately",
                    "1-3 months": "1–3 months",
                    "3-6 months": "3–6 months",
                    Flexible: "Flexible",
                  }}
                  id="rhs_timeline"
                  name="timeline"
                  label="Hiring Timeline"
                  currentValue={form.timeline}
                />
              </div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div
            className="d-flex align-items-center justify-content-between"
            style={{ borderTop: "1px solid #eee", paddingTop: 16, marginTop: 0}}
          >
            <div style={{ fontSize: "12px", color: "#666" }}>
              Your request is confidential. We'll respond within 24 hours.
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#666",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: "8px 10px",
                }}
              >
                Cancel
              </button>
              <CustomButton
                type="submit"
                backgroundColor="#3A6ED4"
                color="#fff"
                text="Submit Request"
                handleClick={handleSubmit}
                style={{ width: 180 }}
              />
            </div>
          </div>

        </form>
      </div>
    </SlidingPane>
  );
};

RequestHiringSupport.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
};

export default RequestHiringSupport;